import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { CRMLayout } from '@/components/layout/CRMLayout'
import { Login } from '@/pages/Login'
import { SetPassword } from '@/pages/SetPassword'
import { CRM } from '@/pages/CRM'

function isRecoveryLink() {
  if (typeof window === 'undefined') return false
  return window.location.hash.includes('type=recovery')
}

function RecoveryGate({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const hashRecovery = location.hash.includes('type=recovery')
  const sessionRecovery = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('supabase_recovery') === 'true'
  if (hashRecovery || sessionRecovery) {
    return <SetPassword />
  }
  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  const sessionRecovery = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('supabase_recovery') === 'true'
  if (user && !isRecoveryLink() && !sessionRecovery) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function AdminGuard({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)

  useEffect(() => {
    if (!user) {
      setIsAdmin(false)
      return
    }
    void supabase
      .from('users')
      .select('is_admin')
      .eq('id', user.id)
      .single()
      .then(
        ({ data }) => setIsAdmin(data?.is_admin === true),
        () => setIsAdmin(false)
      )
  }, [user])

  if (authLoading || isAdmin === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Loading AsterPay CRM...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-4">
        <h1 className="text-2xl font-bold">Access denied</h1>
        <p className="text-muted-foreground text-center max-w-md">
          This app is for AsterPay admins only. If you believe you should have access, contact your team.
        </p>
        <a
          href="https://asterpay.io"
          className="text-primary hover:underline text-sm"
        >
          Go to asterpay.io
        </a>
      </div>
    )
  }

  return <>{children}</>
}

export default function App() {
  return (
    <BrowserRouter>
      <RecoveryGate>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <div className="flex min-h-screen items-center justify-center bg-background p-4">
                <Login />
              </div>
            </PublicRoute>
          }
        />
        <Route
          path="/"
          element={
            <AdminGuard>
              <CRMLayout />
            </AdminGuard>
          }
        >
          <Route index element={<CRM />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </RecoveryGate>
    </BrowserRouter>
  )
}
